import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Loader2, LocateFixed, MapPin, Search, X } from 'lucide-react';
import L from 'leaflet';
import {
  MapContainer,
  Marker,
  TileLayer,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { logger } from 'shared/lib/logger';

import {
  PlaceDetails,
  Prediction,
  SupportedGeoLang,
  autocompleteAddress,
  getPlaceDetails,
  newSessionToken,
  reverseGeocode,
} from 'features/onboarding/services/geocoding.service';
import {
  CurrentLocationError,
  getCurrentLocation,
  isGeolocationSupported,
} from 'features/onboarding/services/browserGeolocation';
import type { SupportedLanguage } from 'shared/i18n/i18n';
import { cn } from 'shared/lib/utils';

// One-time fix for default marker icons under bundlers (CRA/Webpack).
// Without this, marker images 404 because Leaflet uses relative URLs.
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const ISRAEL_CENTER: [number, number] = [31.5, 34.9];
const ISRAEL_ZOOM = 8;
const PINNED_ZOOM = 16;

interface MapPickerModalProps {
  open: boolean;
  initialLat?: number | null;
  initialLng?: number | null;
  initialAddress?: string;
  /**
   * Called when the user confirms a location. `details` is provided when the
   * pin position resolved to a Google place (autocomplete pick or reverse
   * geocode) — callers can use it to auto-fill street/city/etc. It will be
   * `null` only when the user clicked the map at a spot that returned no
   * reverse-geocoding match.
   */
  onConfirm: (lat: number, lng: number, details: PlaceDetails | null) => void;
  onClose: () => void;
}

interface ClickHandlerProps {
  onPick: (lat: number, lng: number) => void;
}

const ClickHandler: React.FC<ClickHandlerProps> = ({ onPick }) => {
  useMapEvents({
    click: (e) => onPick(e.latlng.lat, e.latlng.lng),
  });
  return null;
};

interface RecenterProps {
  lat: number | null;
  lng: number | null;
  zoom?: number;
}

const Recenter: React.FC<RecenterProps> = ({ lat, lng, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (typeof lat === 'number' && typeof lng === 'number') {
      map.setView([lat, lng], zoom ?? map.getZoom(), { animate: true });
    }
  }, [lat, lng, zoom, map]);
  return null;
};

// Leaflet measures the container at mount time. When it mounts inside a
// modal that animates in (framer-motion scale/opacity), the initial
// measurement is wrong and tiles never load. Force a recompute on mount.
const InvalidateOnMount: React.FC = () => {
  const map = useMap();
  useEffect(() => {
    const run = () => map.invalidateSize();
    run();
    const t1 = setTimeout(run, 100);
    const t2 = setTimeout(run, 400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [map]);
  return null;
};

const toGeoLang = (lng: string | undefined): SupportedGeoLang => {
  if (lng === 'ar' || lng === 'en' || lng === 'he') return lng;
  const short = (lng ?? '').slice(0, 2) as SupportedLanguage;
  return short === 'ar' || short === 'en' ? short : 'he';
};

const MapPickerModal: React.FC<MapPickerModalProps> = ({
  open,
  initialLat,
  initialLng,
  initialAddress,
  onConfirm,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const lang = toGeoLang(i18n.language);
  const hasInitial =
    typeof initialLat === 'number' &&
    typeof initialLng === 'number' &&
    !Number.isNaN(initialLat) &&
    !Number.isNaN(initialLng);

  const [lat, setLat] = useState<number | null>(hasInitial ? initialLat! : null);
  const [lng, setLng] = useState<number | null>(hasInitial ? initialLng! : null);
  const [recenterTarget, setRecenterTarget] = useState<{
    lat: number;
    lng: number;
    zoom: number;
  } | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Prediction[]>([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [resolvingPlace, setResolvingPlace] = useState(false);
  const [locating, setLocating] = useState(false);
  const [activeDetails, setActiveDetails] = useState<PlaceDetails | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  const sessionTokenRef = useRef<string>(newSessionToken());
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const suggestAbortRef = useRef<AbortController | null>(null);
  const detailsAbortRef = useRef<AbortController | null>(null);
  const reverseAbortRef = useRef<AbortController | null>(null);
  const triedInitialGeocodeRef = useRef(false);

  const initialCenter = useMemo<[number, number]>(
    () => (hasInitial ? [initialLat!, initialLng!] : ISRAEL_CENTER),
    [hasInitial, initialLat, initialLng],
  );
  const initialZoom = hasInitial ? PINNED_ZOOM : ISRAEL_ZOOM;

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Reset modal state every time it opens. New session token per session so
  // Google bills a complete pick (autocomplete chain + details) as one unit.
  useEffect(() => {
    if (!open) {
      setSearchError(null);
      setSearchQuery('');
      setSuggestions([]);
      setSuggestionsOpen(false);
      setSuggestLoading(false);
      setResolvingPlace(false);
      setLocating(false);
      setActiveDetails(null);
      setActiveIndex(-1);
      triedInitialGeocodeRef.current = false;
      suggestAbortRef.current?.abort();
      detailsAbortRef.current?.abort();
      reverseAbortRef.current?.abort();
      suggestAbortRef.current = null;
      detailsAbortRef.current = null;
      reverseAbortRef.current = null;
      return;
    }
    sessionTokenRef.current = newSessionToken();
    setLat(hasInitial ? initialLat! : null);
    setLng(hasInitial ? initialLng! : null);
    setRecenterTarget(null);
  }, [open, hasInitial, initialLat, initialLng]);

  // When the modal opens with no pin but a typed address, immediately query
  // autocomplete so the first suggestion is one click away.
  useEffect(() => {
    if (!open) return;
    if (hasInitial) return;
    if (triedInitialGeocodeRef.current) return;
    if (!initialAddress || !initialAddress.trim()) return;
    triedInitialGeocodeRef.current = true;
    setSearchQuery(initialAddress);
    setSuggestionsOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Debounced autocomplete.
  useEffect(() => {
    if (!open) return;
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setSuggestLoading(false);
      suggestAbortRef.current?.abort();
      suggestAbortRef.current = null;
      return;
    }
    const handle = setTimeout(async () => {
      suggestAbortRef.current?.abort();
      const ctrl = new AbortController();
      suggestAbortRef.current = ctrl;
      setSuggestLoading(true);
      try {
        const results = await autocompleteAddress(
          q,
          sessionTokenRef.current,
          ctrl.signal,
          lang,
        );
        if (ctrl.signal.aborted) return;
        setSuggestions(results);
      } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError' || ctrl.signal.aborted) return;
        logger.warn('[geocoding] autocomplete failed', err);
        setSuggestions([]);
      } finally {
        if (suggestAbortRef.current === ctrl) {
          setSuggestLoading(false);
          suggestAbortRef.current = null;
        }
      }
    }, 220);
    return () => clearTimeout(handle);
  }, [searchQuery, open, lang]);

  // Highlight follows the result set: reset whenever suggestions change.
  useEffect(() => {
    setActiveIndex(-1);
    optionRefs.current.length = suggestions.length;
  }, [suggestions]);

  const moveActive = (delta: number) => {
    if (suggestions.length === 0) return;
    setSuggestionsOpen(true);
    setActiveIndex((prev) => {
      const next = (prev + delta + suggestions.length) % suggestions.length;
      optionRefs.current[next]?.scrollIntoView({ block: 'nearest' });
      return next;
    });
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveActive(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveActive(-1);
    } else if (e.key === 'Escape' && suggestionsOpen) {
      // Keep the modal open — Escape first dismisses the suggestions.
      e.stopPropagation();
      setSuggestionsOpen(false);
    }
  };

  const handleSelectSuggestion = async (s: Prediction) => {
    setSearchQuery(s.fullText);
    setSuggestionsOpen(false);
    setSearchError(null);

    detailsAbortRef.current?.abort();
    const ctrl = new AbortController();
    detailsAbortRef.current = ctrl;
    setResolvingPlace(true);

    try {
      const details = await getPlaceDetails(
        s.placeId,
        sessionTokenRef.current,
        ctrl.signal,
        lang,
      );
      if (ctrl.signal.aborted) return;
      if (!details) {
        setSearchError(t('businessInfo.geolocation.mapModal.notFound') as string);
        return;
      }
      setLat(details.lat);
      setLng(details.lng);
      setActiveDetails(details);
      setRecenterTarget({ lat: details.lat, lng: details.lng, zoom: PINNED_ZOOM });
      // Selecting a prediction closes the autocomplete session — generate a
      // fresh token so a subsequent edit starts a new billing session.
      sessionTokenRef.current = newSessionToken();
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError' || ctrl.signal.aborted) return;
      logger.warn('[geocoding] place details failed', err);
      setSearchError(t('businessInfo.geolocation.mapModal.searchError') as string);
    } finally {
      if (detailsAbortRef.current === ctrl) {
        setResolvingPlace(false);
        detailsAbortRef.current = null;
      }
    }
  };

  /**
   * Resolve a user-clicked / dragged lat/lng back to a human address. Best
   * effort — if reverse geocoding fails we still keep the coordinates.
   */
  const reverseFromCoords = async (latVal: number, lngVal: number) => {
    reverseAbortRef.current?.abort();
    const ctrl = new AbortController();
    reverseAbortRef.current = ctrl;
    try {
      const details = await reverseGeocode(latVal, lngVal, ctrl.signal, lang);
      if (ctrl.signal.aborted) return;
      if (details) {
        setActiveDetails(details);
        if (details.formattedAddress) setSearchQuery(details.formattedAddress);
      } else {
        setActiveDetails(null);
      }
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError' || ctrl.signal.aborted) return;
      logger.warn('[geocoding] reverse failed', err);
      setActiveDetails(null);
    } finally {
      if (reverseAbortRef.current === ctrl) reverseAbortRef.current = null;
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (suggestions.length === 0) return;
    void handleSelectSuggestion(suggestions[activeIndex >= 0 ? activeIndex : 0]);
  };

  const geolocationSupported = isGeolocationSupported();

  const handleLocateMe = async () => {
    setLocating(true);
    setSearchError(null);
    try {
      const { lat: currentLat, lng: currentLng } = await getCurrentLocation();
      setLat(currentLat);
      setLng(currentLng);
      setActiveDetails(null);
      setRecenterTarget({
        lat: currentLat,
        lng: currentLng,
        zoom: PINNED_ZOOM,
      });
      await reverseFromCoords(currentLat, currentLng);
    } catch (error) {
      const errorKey =
        error instanceof CurrentLocationError
          ? error.translationKey
          : 'locationUnavailable';
      setSearchError(
        t(`businessInfo.geolocation.mapModal.${errorKey}`) as string,
      );
    } finally {
      setLocating(false);
    }
  };

  const canConfirm = typeof lat === 'number' && typeof lng === 'number';

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm(lat!, lng!, activeDetails);
    onClose();
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />

          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ type: 'spring', damping: 26, stiffness: 320 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:max-w-2xl bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col h-[90vh] sm:h-[600px] max-h-[92vh]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="map-picker-title"
          >
            <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-8 h-8 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center flex-shrink-0">
                  <MapPin size={16} />
                </span>
                <h2
                  id="map-picker-title"
                  className="text-base sm:text-lg font-semibold text-gray-900 truncate"
                >
                  {t('businessInfo.geolocation.mapModal.title')}
                </h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                aria-label={t('common.close') as string}
              >
                <X size={18} />
              </button>
            </div>

            {/* Sits above the Leaflet panes (z-index 400+) so the suggestions
                dropdown is not covered by the map. */}
            <div className="relative z-[500] px-5 sm:px-6 py-3 border-b border-gray-100 space-y-2 bg-white">
              <p className="text-xs sm:text-sm text-gray-600">
                {t('businessInfo.geolocation.mapModal.instructions')}
              </p>

              <form onSubmit={handleSearchSubmit} className="relative">
                <div className="relative">
                  <span className="absolute inset-y-0 start-0 flex items-center ps-3.5 text-gray-400 pointer-events-none">
                    {suggestLoading || resolvingPlace ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Search size={16} />
                    )}
                  </span>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setSuggestionsOpen(true);
                    }}
                    onFocus={() => setSuggestionsOpen(true)}
                    onBlur={() => {
                      setTimeout(() => setSuggestionsOpen(false), 150);
                    }}
                    onKeyDown={handleSearchKeyDown}
                    placeholder={
                      t(
                        'businessInfo.geolocation.mapModal.searchPlaceholder',
                      ) as string
                    }
                    className="w-full ps-11 pe-10 py-2.5 text-sm rounded-xl border border-gray-200 bg-gray-50 text-gray-900 placeholder:text-gray-400 transition-colors focus:outline-none focus:bg-white focus:border-haat-red-400 focus:ring-4 focus:ring-haat-red-100"
                    role="combobox"
                    aria-expanded={suggestionsOpen && suggestions.length > 0}
                    aria-controls="map-picker-suggestions"
                    aria-autocomplete="list"
                    aria-activedescendant={
                      activeIndex >= 0
                        ? `map-picker-suggestion-${activeIndex}`
                        : undefined
                    }
                  />
                  {searchQuery.length > 0 && (
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setSearchQuery('');
                        setSuggestions([]);
                        setSuggestionsOpen(false);
                      }}
                      className="absolute inset-y-0 end-0 flex items-center pe-3.5 text-gray-400 hover:text-gray-600 transition-colors"
                      aria-label={t('businessInfo.geolocation.clear') as string}
                    >
                      <X size={15} />
                    </button>
                  )}
                </div>
                {suggestionsOpen && searchQuery.trim().length >= 2 && (
                  <div
                    id="map-picker-suggestions"
                    role="listbox"
                    className="absolute z-10 mt-2 w-full overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-xl ring-1 ring-black/5"
                  >
                    {suggestLoading && suggestions.length === 0 ? (
                      <div className="flex items-center gap-2.5 px-4 py-3.5 text-sm text-gray-500">
                        <Loader2 size={15} className="animate-spin" />
                        {t('common.loading')}
                      </div>
                    ) : suggestions.length === 0 ? (
                      <div className="flex items-center gap-2.5 px-4 py-3.5 text-sm text-gray-500">
                        <Search size={15} className="text-gray-300" />
                        {t('businessInfo.geolocation.mapModal.noResults')}
                      </div>
                    ) : (
                      <ul
                        className="max-h-64 overflow-y-auto py-1.5"
                        onMouseLeave={() => setActiveIndex(-1)}
                      >
                        {suggestions.map((s, index) => (
                          <li key={s.placeId}>
                            <button
                              id={`map-picker-suggestion-${index}`}
                              ref={(el) => {
                                optionRefs.current[index] = el;
                              }}
                              type="button"
                              role="option"
                              aria-selected={index === activeIndex}
                              onMouseDown={(e) => e.preventDefault()}
                              onMouseEnter={() => setActiveIndex(index)}
                              onClick={() => void handleSelectSuggestion(s)}
                              className={cn(
                                'w-full flex items-center gap-3 px-3 py-2.5 text-start transition-colors',
                                index === activeIndex
                                  ? 'bg-haat-red-50'
                                  : 'bg-transparent',
                              )}
                            >
                              <span
                                className={cn(
                                  'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-colors',
                                  index === activeIndex
                                    ? 'bg-haat-red-100 text-haat-red-600'
                                    : 'bg-gray-100 text-gray-400',
                                )}
                              >
                                <MapPin size={15} />
                              </span>
                              <span className="flex min-w-0 flex-col">
                                <span className="truncate text-sm font-medium text-gray-900">
                                  {s.mainText || s.fullText}
                                </span>
                                {s.secondaryText && (
                                  <span className="truncate text-xs text-gray-500">
                                    {s.secondaryText}
                                  </span>
                                )}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </form>

              <button
                type="button"
                onClick={() => void handleLocateMe()}
                disabled={locating || !geolocationSupported}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {locating ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <LocateFixed size={16} />
                )}
                {t(
                  locating
                    ? 'businessInfo.geolocation.mapModal.locating'
                    : 'businessInfo.geolocation.mapModal.locateMe',
                )}
              </button>

              {(searchError || !geolocationSupported) && (
                <p className="text-xs text-amber-700">
                  {searchError ??
                    t(
                      'businessInfo.geolocation.mapModal.locationUnavailable',
                    )}
                </p>
              )}

              <p className="text-[10px] text-gray-400 text-end">
                {t('businessInfo.geolocation.mapModal.poweredBy')}
              </p>
            </div>

            <div className="relative z-0 isolate flex-1 min-h-0 w-full">
              <MapContainer
                center={initialCenter}
                zoom={initialZoom}
                scrollWheelZoom
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <InvalidateOnMount />
                <ClickHandler
                  onPick={(latVal, lngVal) => {
                    setLat(latVal);
                    setLng(lngVal);
                    void reverseFromCoords(latVal, lngVal);
                  }}
                />
                {recenterTarget && (
                  <Recenter
                    lat={recenterTarget.lat}
                    lng={recenterTarget.lng}
                    zoom={recenterTarget.zoom}
                  />
                )}
                {typeof lat === 'number' && typeof lng === 'number' && (
                  <Marker
                    position={[lat, lng]}
                    draggable
                    eventHandlers={{
                      dragend: (e) => {
                        const m = e.target as L.Marker;
                        const pos = m.getLatLng();
                        setLat(pos.lat);
                        setLng(pos.lng);
                        void reverseFromCoords(pos.lat, pos.lng);
                      },
                    }}
                  />
                )}
              </MapContainer>
            </div>

            <div className="px-5 sm:px-6 py-3 border-t border-gray-100 bg-gray-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-xs sm:text-sm text-gray-700 min-w-0">
                {canConfirm ? (
                  <span className="inline-flex items-center gap-1.5 font-medium text-emerald-700">
                    <CheckCircle2 size={14} />
                    {t('businessInfo.geolocation.mapModal.selected', {
                      lat: lat!.toFixed(5),
                      lng: lng!.toFixed(5),
                    })}
                  </span>
                ) : (
                  <span className="text-gray-500">
                    {t('businessInfo.geolocation.mapModal.noSelection')}
                  </span>
                )}
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 rounded-xl text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-100 transition-colors"
                >
                  {t('businessInfo.geolocation.mapModal.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={!canConfirm}
                  className={cn(
                    'inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white shadow-sm transition-colors',
                    'bg-haat-red-600 hover:bg-haat-red-700 disabled:opacity-60 disabled:cursor-not-allowed',
                  )}
                >
                  <CheckCircle2 size={16} />
                  {t('businessInfo.geolocation.mapModal.confirm')}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default MapPickerModal;
